<template>
  <div>
    <provisioning-qos-status @refresh="load" qos-type="QOS" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>QoS一覧</span>
        <manual-button path="/qos/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="qos.search" v-model="qos.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn :href="downloadUrl" color="primary">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="qos.sortBy"
          primary-key="vpnVnCode"
          :fields="qos.fields"
          :items="qos.items"
          v-model="qos.selected"
          :filter-option="qos.filter_option"
          :filter="qos.filter"
          :hover="true"
          sticky-header="50vh"
          @click:row="showDetails"
        >
          <!-- 仮想ネットワーク名 -->
          <template #cell(vnName)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <!-- VLAN -->
          <template #cell(vLan)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <!-- 回線数 -->
          <template #cell(line)="data">
            <template v-if="data.item.vpnVnCode.startsWith('B')">
              {{ data.item.numberOfVnConnect }}
            </template>
            <template v-else>
              {{
                `${data.item.numberOfVnConnect}/${data.item.upperLimitVnConnect}`
              }}
            </template>
          </template>
          <!-- 設定可否情報 -->
          <template #cell(qosControl)="data">
            {{ $filter.enumTo(data.item.qosControl, "qosControl") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { QosInfo } from "@/apis/QosApi";
import QosSettingViewRC from "@/modals/qos/QosSettingViewRC.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "QosSettingList",
  data() {
    return {
      qos: {
        sortBy: { key: "vpnVnCode", order: "desc" } as SortItem,
        fields: [
          {
            key: "vpnVnCode",
            title: "VPN/VNコード",
            sortable: true,
            width: "150px",
          },
          {
            key: "vnName",
            title: "仮想ネットワーク名",
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: QosInfo) => {
              return item.vpnVnCode.startsWith("B") ? null : value;
            },
          },
          {
            key: "vLan",
            title: "VLAN",
            width: "150px",
            align: "end",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: null, key: null, item: QosInfo) => {
              if (
                item.vpnVnCode.startsWith("B") ||
                item.vpnVnCode.startsWith("D")
              ) {
                return null;
              } else {
                if (item.vlanIdType === "UNTAG") {
                  return "Untag";
                } else if (item.vlanIdType === "OTHER") {
                  return "Other";
                } else {
                  return `${item.vlanId}`;
                }
              }
            },
          },
          {
            key: "qosType",
            title: "QoS方式",
            width: "130px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) => {
              if (value === "NONE") {
                return "-";
              } else {
                return this.$filter.enumTo(value, "qosType");
              }
            },
          },
          {
            key: "priorityTarget",
            title: "優先度参照先",
            width: "180px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) => {
              if (value === "NONE") {
                return "-";
              } else {
                return this.$filter.enumTo(value, "qos_PriorityTarget");
              }
            },
          },
          { key: "line", title: "回線数", width: "80px" },
          {
            key: "qosControl",
            title: "設定可/不可",
            width: "120px",
            align: "center",
          },
        ] as AppTableHeader<QosInfo>[],
        items: [] as QosInfo[],
        selected: [] as QosInfo[],
        search: {
          vpnVnCode: { type: "text", label: "VPN/VNコード" },
          vnName: { type: "text", label: "仮想ネットワーク名" },
          vLan: { type: "text", label: "VLAN" },
          qosType: {
            type: "select",
            label: "QoS方式",
            items: [
              { value: "ABSOLUTE", text: "絶対優先方式" },
              { value: "BANDWIDTH", text: "帯域制御方式" },
              { value: "NONE", text: "-" },
            ],
          },
          priorityTarget: {
            type: "select",
            label: "優先度参照先",
            items: [
              { value: "IPPRECEDENCE", text: "IP Precedence" },
              { value: "COS", text: "CoS" },
              { value: "NONE", text: "-" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          vpnVnCode: "like",
          vnName: "like",
          vLan: "like",
          qosType: "equal",
          priorityTarget: "equal",
        } as FilterOption,
      },
      // 初期通信完了フラグ
      isLoaded: false,
      // download用URL
      downloadUrl: this.$api.qosApi.getCsvQosURL(),
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.qos.items = await this.$api.qosApi.getQoSInfoList();
    },
    async showDetails(item: QosInfo) {
      const qosDetail = await this.$api.qosApi.getQosDetail(item.vpnVnCode);
      await this.$modal.show(QosSettingViewRC, {
        vpnVnCode: item.vpnVnCode,
        vnName: item.vnName,
        isNew: qosDetail.qosType === "NONE",
        qosDetail: qosDetail,
      });
      await this.load();
    },
  },
});
</script>
